import { ActionSheetOptions } from '@expo/react-native-action-sheet'
import { useStyledActionSheet } from './useStyledActionSheet'

const CANCEL_OPTION = 'Cancel'

interface ShowDestructiveActionSheetParams {
  title: string
  message: string
  label: string
  handler: () => void
  // Called once the sheet has been dismissed (after either option is chosen). iOS
  // invokes this action-sheet callback only after the native sheet has finished
  // dismissing, which makes it a safe point to tear down an underlying popup/modal.
  onClosed?: () => void
}

interface ShowActionSheetParams<OptionType> {
  title: string
  options: OptionType[]
  actionSheetStyleOptions?: Omit<ActionSheetOptions, 'options'>
  callback: (option: OptionType) => void
  onCancel?: () => void
}

export const useApplicationActionSheet = <OptionType extends string>() => {
  const { showActionSheetWithOptions } = useStyledActionSheet()

  const showDestructiveActionSheet = ({
    title,
    message,
    label,
    handler,
    onClosed,
  }: ShowDestructiveActionSheetParams): void => {
    const options = [label, CANCEL_OPTION]
    const destructiveButtonIndex = 0
    const cancelButtonIndex = 1

    showActionSheetWithOptions(
      {
        title,
        message,
        options,
        cancelButtonIndex,
        destructiveButtonIndex,
      },
      (buttonIndex) => {
        if ((buttonIndex as number) < 1) {
          handler()
        }
        onClosed?.()
      },
    )
  }

  const showActionSheet = ({
    title,
    options,
    actionSheetStyleOptions,
    callback,
    onCancel,
  }: ShowActionSheetParams<OptionType>) => {
    const allOptions = [...options, CANCEL_OPTION]
    const cancelButtonIndex = allOptions.length - 1

    showActionSheetWithOptions(
      {
        title,
        options: allOptions,
        cancelButtonIndex,
        ...actionSheetStyleOptions,
      },
      (buttonIndex) => {
        if (buttonIndex === cancelButtonIndex) {
          onCancel?.()
          return
        }

        if (typeof callback === 'function') {
          callback(allOptions[buttonIndex as number] as OptionType)
        }
      },
    )
  }

  return {
    showActionSheet,
    showDestructiveActionSheet,
  }
}
